import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  ArrowLeft,
  Edit,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Webhook,
  ExternalLink,
  Copy,
} from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  useWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useWebhookDeliveries,
  useRetryDelivery,
} from "@/hooks/use-webhooks"
import type { WebhookDelivery } from "@/lib/types"

const EVENT_LABELS: Record<string, string> = {
  deal_created: "Nowy deal",
  deal_updated: "Aktualizacja deala",
  deal_phase_changed: "Zmiana fazy deala",
  deal_won: "Deal wygrany",
  deal_lost: "Deal przegrany",
  task_created: "Nowe zadanie",
  task_completed: "Zadanie ukończone",
  contact_created: "Nowy kontakt",
  company_created: "Nowa firma",
  service_ticket_created: "Nowe zgłoszenie",
  service_ticket_phase_changed: "Zmiana fazy zgłoszenia",
  service_ticket_resolved: "Zgłoszenie rozwiązane",
  invoice_issued: "Faktura wystawiona",
  invoice_paid: "Faktura opłacona",
  quote_accepted: "Oferta zaakceptowana",
  quote_rejected: "Oferta odrzucona",
  "webhook.test": "Test webhook",
}

export function WebhookDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<"overview" | "deliveries">("overview")
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState<string>("")

  const { data, isLoading } = useWebhook(id ?? "")
  const deleteMutation = useDeleteWebhook()
  const testMutation = useTestWebhook()
  const { data: deliveriesData } = useWebhookDeliveries(
    id ?? "",
    deliveryStatusFilter || undefined
  )

  const webhook = data?.data
  const deliveries = deliveriesData?.data ?? []

  const handleDelete = () => {
    if (!webhook) return
    if (!confirm(`Usunąć webhook "${webhook.name}"?`)) return
    deleteMutation.mutate(webhook.id, {
      onSuccess: () => {
        toast.success("Webhook usunięty")
        navigate("/webhooks")
      },
    })
  }

  const handleTest = () => {
    if (!webhook) return
    testMutation.mutate(webhook.id, {
      onSuccess: (res) => {
        if (res.data.success) {
          toast.success(`Test OK (HTTP ${res.data.status})`)
        } else {
          toast.error(
            res.data.error ?? `Test nieudany (HTTP ${res.data.status})`
          )
        }
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Błąd"),
    })
  }

  const copyUrl = () => {
    if (!webhook) return
    navigator.clipboard.writeText(webhook.url)
    toast.success("URL skopiowany")
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-48 rounded animate-pulse bg-muted" />
        <div className="h-40 rounded-lg animate-pulse bg-muted" />
      </div>
    )
  }

  if (!webhook) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Nie znaleziono webhooka</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/webhooks")}
            className="p-2 rounded-lg hover:opacity-80 text-muted-foreground"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {webhook.name}
              </h1>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  webhook.is_active
                    ? "bg-green-500/10 text-green-500"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {webhook.is_active ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            {webhook.description && (
              <p className="text-sm mt-0.5 text-muted-foreground">
                {webhook.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={testMutation.isPending}
          >
            <Send size={14} />
            {testMutation.isPending ? "Testowanie..." : "Test"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/webhooks/${id}/edit`)}
          >
            <Edit size={14} />
            Edytuj
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          { key: "overview" as const, label: "Przegląd" },
          { key: "deliveries" as const, label: `Dostawy (${webhook.deliveries_count})` },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <OverviewTab webhook={webhook} onCopyUrl={copyUrl} />
      ) : (
        <DeliveriesTab
          webhookId={webhook.id}
          deliveries={deliveries}
          statusFilter={deliveryStatusFilter}
          onStatusFilterChange={setDeliveryStatusFilter}
        />
      )}
    </div>
  )
}

function OverviewTab({
  webhook,
  onCopyUrl,
}: {
  webhook: NonNullable<ReturnType<typeof useWebhook>["data"]>["data"]
  onCopyUrl: () => void
}) {
  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Main info */}
      <div className="col-span-2 space-y-4">
        {/* URL */}
        <div className="rounded-lg border p-4 border-border bg-card">
          <h3 className="text-xs font-medium uppercase mb-2 text-muted-foreground">
            Endpoint URL
          </h3>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm px-3 py-2 rounded-lg font-mono truncate bg-muted text-foreground">
              {webhook.url}
            </code>
            <button
              onClick={onCopyUrl}
              className="p-2 rounded hover:opacity-80 text-muted-foreground"
            >
              <Copy size={16} />
            </button>
            <a
              href={webhook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded hover:opacity-80 text-muted-foreground"
            >
              <ExternalLink size={16} />
            </a>
          </div>
        </div>

        {/* Events */}
        <div className="rounded-lg border p-4 border-border bg-card">
          <h3 className="text-xs font-medium uppercase mb-3 text-muted-foreground">
            Subskrybowane zdarzenia ({webhook.subscribed_events.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {webhook.subscribed_events.map((event) => (
              <span
                key={event}
                className="text-xs px-2 py-1 rounded-full text-primary bg-primary/[0.08]"
              >
                {EVENT_LABELS[event] ?? event}
              </span>
            ))}
          </div>
        </div>

        {/* Custom Headers */}
        {Object.keys(webhook.headers || {}).length > 0 && (
          <div className="rounded-lg border p-4 border-border bg-card">
            <h3 className="text-xs font-medium uppercase mb-3 text-muted-foreground">
              Dodatkowe nagłówki
            </h3>
            <div className="space-y-1">
              {Object.entries(webhook.headers).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm font-mono">
                  <span className="text-primary">{key}:</span>
                  <span className="text-foreground">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Deliveries */}
        {webhook.recent_deliveries.length > 0 && (
          <div className="rounded-lg border p-4 border-border bg-card">
            <h3 className="text-xs font-medium uppercase mb-3 text-muted-foreground">
              Ostatnie dostawy
            </h3>
            <div className="space-y-2">
              {webhook.recent_deliveries.map((d) => (
                <DeliveryRow key={d.id} delivery={d} webhookId={webhook.id} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <div className="rounded-lg border p-4 space-y-3 border-border bg-card">
          <h3 className="text-xs font-medium uppercase text-muted-foreground">
            Informacje
          </h3>
          <InfoRow label="Status" value={webhook.is_active ? "Aktywny" : "Nieaktywny"} />
          <InfoRow label="Secret" value={webhook.secret ? "Skonfigurowany" : "Brak"} />
          <InfoRow label="Błędy" value={String(webhook.failure_count)} />
          <InfoRow
            label="Ostatnie wyzwolenie"
            value={
              webhook.last_triggered_at
                ? new Date(webhook.last_triggered_at).toLocaleString("pl-PL")
                : "Nigdy"
            }
          />
          <InfoRow
            label="Utworzono"
            value={new Date(webhook.created_at).toLocaleString("pl-PL")}
          />
          <InfoRow
            label="Zaktualizowano"
            value={new Date(webhook.updated_at).toLocaleString("pl-PL")}
          />
          <InfoRow label="Dostawy" value={String(webhook.deliveries_count)} />
        </div>

        {webhook.failure_count > 0 && (
          <div className="rounded-lg border p-4 border-amber-500 bg-amber-500/5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <span className="text-sm font-medium text-amber-500">
                Uwaga
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Webhook ma {webhook.failure_count} kolejnych błędów.
              {webhook.failure_count >= 10 && " Zostanie automatycznie dezaktywowany po 10 błędach."}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">
        {label}
      </span>
      <span className="text-xs font-medium text-foreground">
        {value}
      </span>
    </div>
  )
}

function DeliveriesTab({
  webhookId,
  deliveries,
  statusFilter,
  onStatusFilterChange,
}: {
  webhookId: string
  deliveries: WebhookDelivery[]
  statusFilter: string
  onStatusFilterChange: (s: string) => void
}) {
  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-1 rounded-lg p-1 bg-muted">
        {[
          { key: "", label: "Wszystkie" },
          { key: "success", label: "Sukces" },
          { key: "failed", label: "Błąd" },
          { key: "pending", label: "Oczekujące" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => onStatusFilterChange(f.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === f.key
                ? "bg-card text-foreground"
                : "text-muted-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {deliveries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 rounded-lg border border-dashed border-border">
          <Webhook size={40} className="text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Brak dostaw
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {deliveries.map((d) => (
            <DeliveryRow key={d.id} delivery={d} webhookId={webhookId} />
          ))}
        </div>
      )}
    </div>
  )
}

function DeliveryRow({
  delivery,
  webhookId,
  compact,
}: {
  delivery: WebhookDelivery
  webhookId: string
  compact?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const retryMutation = useRetryDelivery()

  const statusConfig = {
    success: { icon: CheckCircle2, color: "#22c55e", label: "Sukces" },
    failed: { icon: XCircle, color: "#ef4444", label: "Błąd" },
    pending: { icon: Clock, color: "#f59e0b", label: "Oczekujące" },
  }[delivery.status] ?? { icon: Clock, color: "#9ca3af", label: delivery.status }

  const StatusIcon = statusConfig.icon

  const handleRetry = () => {
    retryMutation.mutate(
      { webhookId, deliveryId: delivery.id },
      {
        onSuccess: () => toast.success("Ponowienie zaplanowane"),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Błąd"),
      }
    )
  }

  return (
    <div
      className="rounded-lg border p-3 cursor-pointer transition-colors border-border bg-card"
      onClick={() => !compact && setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StatusIcon size={16} style={{ color: statusConfig.color }} />
          <span className="text-sm font-medium text-foreground">
            {EVENT_LABELS[delivery.event_name] ?? delivery.event_name}
          </span>
          {delivery.response_status && (
            <span
              className="text-xs px-1.5 py-0.5 rounded font-mono"
              style={{
                backgroundColor:
                  delivery.response_status >= 200 && delivery.response_status < 300
                    ? "rgba(34,197,94,0.1)"
                    : "rgba(239,68,68,0.1)",
                color:
                  delivery.response_status >= 200 && delivery.response_status < 300
                    ? "#22c55e"
                    : "#ef4444",
              }}
            >
              {delivery.response_status}
            </span>
          )}
          {delivery.duration_ms !== null && (
            <span className="text-xs text-muted-foreground">
              {delivery.duration_ms}ms
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {delivery.attempts > 1 && (
            <span className="text-xs text-muted-foreground">
              próba {delivery.attempts}/{delivery.max_attempts}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {new Date(delivery.created_at).toLocaleString("pl-PL")}
          </span>
          {!compact && delivery.status === "failed" && delivery.attempts < delivery.max_attempts && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleRetry()
              }}
              disabled={retryMutation.isPending}
              className="p-1 rounded hover:opacity-80 text-primary"
              title="Ponów"
            >
              <RefreshCw size={14} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2 border-border">
          {delivery.error_message && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Błąd:
              </span>
              <p className="text-xs font-mono mt-0.5 text-red-500">
                {delivery.error_message}
              </p>
            </div>
          )}
          {delivery.next_retry_at && delivery.status === "failed" && (
            <div className="text-xs text-muted-foreground">
              Następna próba: {new Date(delivery.next_retry_at).toLocaleString("pl-PL")}
            </div>
          )}
          {delivery.completed_at && (
            <div className="text-xs text-muted-foreground">
              Zakończono: {new Date(delivery.completed_at).toLocaleString("pl-PL")}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
